<script setup lang="ts">
const model = defineModel<string>({ default: '' })

const props = withDefaults(defineProps<{
  icon?: string
  type?: 'text' | 'email' | 'password' | 'tel'
  id?: string
  name?: string
  placeholder?: string
  autocomplete?: string
  multiline?: boolean
  rows?: number
  maxlength?: number
  hasError?: boolean
  shake?: boolean
  loading?: boolean
  autoGrow?: boolean
  readonly?: boolean
}>(), {
  type: 'text',
  rows: 1,
})

const emit = defineEmits<{
  blur: [event: FocusEvent]
  input: [event: Event]
  animationend: [event: AnimationEvent]
}>()

const inputEl = ref<HTMLInputElement | HTMLTextAreaElement | null>(null)

defineExpose({ inputEl })

function growTextarea() {
  if (!props.autoGrow || inputEl.value?.tagName !== 'TEXTAREA') return
  const el = inputEl.value as HTMLTextAreaElement
  el.style.overflow = 'hidden'
  el.style.height = 'auto'
  el.style.height = `${el.scrollHeight}px`
}

function handleInput(event: Event) {
  growTextarea()
  emit('input', event)
}

watch(model, () => nextTick(growTextarea))

onMounted(() => nextTick(growTextarea))
</script>

<template>
  <div
    class="input-wrapper"
    :class="{
      'input-wrapper--textarea': multiline && !autoGrow,
      'input-wrapper--autogrow': multiline && autoGrow,
      'has-error': hasError,
      'input-wrapper--readonly': readonly,
      shake,
    }"
    @animationend="$emit('animationend', $event)"
  >
    <Icon v-if="icon" :name="icon" size="1.1rem" class="field-icon" />
    <textarea
      v-if="multiline"
      :id="id"
      ref="inputEl"
      v-model="model"
      :name="name"
      :rows="autoGrow ? 1 : rows"
      :placeholder="placeholder"
      :maxlength="maxlength"
      :autocomplete="autocomplete"
      :readonly="readonly"
      @blur="$emit('blur', $event)"
      @input="handleInput"
    />
    <input
      v-else
      :id="id"
      ref="inputEl"
      v-model="model"
      :type="type"
      :name="name"
      :autocomplete="autocomplete"
      :placeholder="placeholder"
      :maxlength="maxlength"
      :readonly="readonly"
      @blur="$emit('blur', $event)"
      @input="handleInput"
    />
    <Icon
      v-if="loading"
      name="tabler:loader-2"
      size="1.2rem"
      class="input-loading"
    />
  </div>
</template>
